/**
 * Infinite-scroll trigger (2026-07-28). Calls `onVisible` when it scrolls into
 * view, which the parent turns into "fetch the next batch and append".
 *
 * `IntersectionObserver` rather than a scroll handler: no per-frame work, and
 * it fires correctly inside a table's own overflow container as well as in
 * ordinary page flow.
 *
 * The observer is rebuilt whenever `loading` flips, and that is load-bearing
 * rather than incidental (2026-07-29). An observer only fires on a CHANGE in
 * intersection, so after a batch landed while the sentinel was still on screen
 * — a short table, a tall window, or a page of rows that doesn't fill the
 * viewport — no further callback ever came and loading stalled until the
 * operator scrolled again by hand. Re-observing delivers a fresh initial
 * callback for an element that is still visible, so the next batch loads
 * immediately and the chain continues on its own to the end of the table.
 *
 * Shared by the `/data` browser (inside the table's scroll container) and the
 * Home audit-log card (in page flow), because the observer-rebuild subtlety
 * above is exactly the kind of detail a second copy loses.
 */

import { useEffect, useRef } from "react";

export function LoadMoreSentinel({
  onVisible,
  loading,
}: {
  readonly onVisible: () => void;
  readonly loading: boolean;
}): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    // While a fetch is in flight there is nothing to trigger — the parent
    // would ignore the call anyway. Re-arm once it settles.
    if (loading) return undefined;
    const el = ref.current;
    if (el === null || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) cb.current();
      },
      // Start fetching a little before the row actually appears so the next
      // batch is usually already there by the time the operator reaches it.
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading]);
  return (
    <div ref={ref} className="data-browser-load-more" aria-hidden="true">
      {loading ? "Loading…" : ""}
    </div>
  );
}
